import React, { useEffect, useId, useState } from 'react';
import {
  reportControlBridgeIssue,
  ControlBridgeReportIssueError,
  type ReportIssueKind,
  type ReportIssueSeverity,
} from '../../../services/controlBridgeReportIssueService';
import styles from '../ControlBridge.module.css';

export interface ReportIssueDialogProps {
  open: boolean;
  workspaceSlug: string;
  surface?: string;
  scopeId?: string;
  onClose: () => void;
  /** Called with the created item id after a successful save. */
  onSubmitted?: (itemId: string) => void | Promise<void>;
}

export const ReportIssueDialog: React.FC<ReportIssueDialogProps> = ({
  open,
  workspaceSlug,
  surface,
  scopeId,
  onClose,
  onSubmitted,
}) => {
  const titleId = useId();
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<ReportIssueSeverity>('medium');
  const [issueType, setIssueType] = useState<ReportIssueKind>('defect');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdState, setCreatedState] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSummary('');
    setDescription('');
    setSeverity('medium');
    setIssueType('defect');
    setBusy(false);
    setError(null);
    setCreatedId(null);
    setCreatedState(null);
  }, [open]);

  if (!open) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = summary.trim();
    if (!trimmed) {
      setError('Summary is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await reportControlBridgeIssue(workspaceSlug, {
        summary: trimmed,
        description: description.trim() || undefined,
        severity,
        issueType,
        surface,
        scopeId,
      });
      setCreatedId(result.itemId);
      setCreatedState(result.currentState?.trim() || 'CREATED');
      try {
        await onSubmitted?.(result.itemId);
      } catch {
        /* Persist already succeeded; inbox refresh is best-effort. */
      }
    } catch (err) {
      const message =
        err instanceof ControlBridgeReportIssueError
          ? err.message
          : 'Failed to report issue';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.reportIssueBackdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.reportIssueDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="report-issue-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId} className={styles.reportIssueTitle}>
          Report issue
        </h2>
        <p className={styles.reportIssueHelp}>
          Lands on <strong>{workspaceSlug}</strong> immediately. A one-line summary is enough.
        </p>
        {createdId ? (
          <div className={styles.reportIssueSuccess} role="status">
            Recorded as{' '}
            <button
              type="button"
              className={styles.issuesInboxId}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                font: 'inherit',
              }}
              data-testid="report-issue-created-id"
              onClick={onClose}
            >
              <code>{createdId}</code>
            </button>
            . Open it from Diagnostics.
          </div>
        ) : (
          <form onSubmit={submit}>
            <label className={styles.reportIssueLabel} htmlFor="cb-report-issue-summary">
              Summary
            </label>
            <input
              id="cb-report-issue-summary"
              className={styles.reportIssueInput}
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              required
              autoFocus
              autoComplete="off"
              maxLength={200}
              placeholder="What is not working?"
            />
            <label className={styles.reportIssueLabel} htmlFor="cb-report-issue-description">
              Details (optional)
            </label>
            <textarea
              id="cb-report-issue-description"
              className={styles.reportIssueTextarea}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              placeholder="What you expected, and where you were."
            />
            <div className={styles.reportIssueRow}>
              <label className={styles.reportIssueLabel} htmlFor="cb-report-issue-severity">
                Severity
              </label>
              <select
                id="cb-report-issue-severity"
                className={styles.reportIssueSelect}
                value={severity}
                onChange={(event) => setSeverity(event.target.value as ReportIssueSeverity)}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <label className={styles.reportIssueLabel} htmlFor="cb-report-issue-type">
                Type
              </label>
              <select
                id="cb-report-issue-type"
                className={styles.reportIssueSelect}
                value={issueType}
                onChange={(event) => setIssueType(event.target.value as ReportIssueKind)}
              >
                <option value="defect">Defect</option>
                <option value="gap">Gap</option>
                <option value="observation">Observation</option>
              </select>
            </div>
            {error ? (
              <p className={styles.reportIssueError} role="alert">
                {error}
              </p>
            ) : null}
            <div className={styles.reportIssueActions}>
              <button type="button" className={styles.reportIssueSecondary} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className={styles.reportIssuePrimary} disabled={busy}>
                {busy ? 'Saving…' : 'Save issue'}
              </button>
            </div>
          </form>
        )}
        {createdId ? (
          <div className={styles.reportIssueActions}>
            <button type="button" className={styles.reportIssuePrimary} onClick={onClose}>
              View in Issues
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
